import { State } from "infrontjs";

import questionTemplate from "./SurveyQuestionTemplate.html";
import doneTemplate from "./SurveyDoneTemplate.html";
import messageTemplate from "./SurveyMessageTemplate.html";
import { openOverlay, closeOverlay, appHomeRoute } from "./SurveyOverlay";
import { _lcs, currentLanguage, locale } from "../../i18n";

// Faces for the 1–5 smiley scale, worst → best. Labels are German source
// strings (dictionary keys), translated where they are read.
const SMILEYS = [
    { value : 1, emoji : "😞", label : "Sehr schlecht" },
    { value : 2, emoji : "🙁", label : "Schlecht" },
    { value : 3, emoji : "😐", label : "Geht so" },
    { value : 4, emoji : "🙂", label : "Gut" },
    { value : 5, emoji : "😍", label : "Sehr gut" }
];

// How long a tap shows its "picked" animation before the next question
// slides in — long enough to see the choice land, short enough to feel snappy.
const ADVANCE_MS = 350;

// Rough answering time per question for the "ca. 20 Sek." hint: every
// question is a single tap, five seconds includes reading it.
const SECONDS_PER_QUESTION = 5;

// Unsent answers per token, kept in localStorage so a dead network (a guest
// on the train home) loses nothing — see _flush().
const QUEUE_KEY = "surveyQueue";

/**
 * SurveyState — a guest answers a survey (`#/survey/:token`).
 *
 * The target of the link the host sends (chat, QR, later a push after
 * departure). No auth guard: the token is the credential, and the guest is
 * most likely logged out by then. Renders into its own full-screen layer
 * (SurveyOverlay) so it works in both shell modes.
 *
 * Built to be finished on a phone in under a minute without typing: one
 * question per screen, a tap answers and moves on, the progress is a hiking
 * trail towards the summit, and a small celebration at the end. Every tap is
 * stored right away (PUT per answer), so stopping halfway still counts, and
 * reopening the link continues at the first unanswered question.
 */
export class SurveyState extends State
{
    static ROUTE = "/survey/:token";
    static ID = "survey";

    showsBottomNav() { return false; }

    async enter()
    {
        this.token = this.routeParams.getParam( "token", "" );
        this.root = openOverlay();
        this.reducedMotion = !!( window.matchMedia && window.matchMedia( "(prefers-reduced-motion: reduce)" ).matches );
        this.busy = false;

        // One delegated listener for every screen this state paints.
        this.addEventListener( this.root, "click", ( e ) => this._onClick( e ) );
        this.addEventListener( this.root, "input", ( e ) => this._onInput( e ) );
        this.addEventListener( window, "online", () => this._flush() );

        await this._load();
    }

    async exit()
    {
        closeOverlay();
    }

    /* --------------------------------- Load ---------------------------------- */

    async _load()
    {
        this._message( { icon : "mountain", title : _lcs( "Umfrage wird geladen …" ), loading : true } );

        if ( !this.token )
        {
            return this._showError( 404 );
        }

        try
        {
            const { result } = await this.app.api.get(
                "/survey/invite/" + encodeURIComponent( this.token ) + "?lang=" + encodeURIComponent( currentLanguage() ) );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected survey response." );
            }
            this.survey = result.data;
        }
        catch ( e )
        {
            console.error( e );
            return this.app ? this._showError( +e?.status ) : undefined;
        }

        // Disposal nulls this.app — the guest may have left while it loaded.
        if ( !this.app )
        {
            return;
        }

        // Answers the server has, plus any still waiting in the offline queue.
        this.answers = { ...( this.survey.answers || {} ), ...this._queue() };
        this.questions = this.survey.questions || [];

        if ( this.survey.completedAt )
        {
            return this._showDone( true );
        }
        if ( this.questions.length === 0 )
        {
            return this._showError( 410 );
        }

        // Resume where the guest stopped; all answered but not submitted → submit.
        const first = this.questions.findIndex( ( q ) => this.answers[ q.id ] === undefined );
        if ( first < 0 )
        {
            return this._finish();
        }
        this.index = first;
        this._flush();
        this._renderQuestion();
    }

    /* -------------------------------- Screens -------------------------------- */

    _renderQuestion()
    {
        const q = this.questions[ this.index ];
        const remaining = this.questions.length - this.index;
        this.chipsDraft = q.type === "chips" && Array.isArray( this.answers[ q.id ] ) ? this.answers[ q.id ].slice() : [];
        this.commentOpen = q.type === "comment" && typeof this.answers[ q.id ] === "string" && this.answers[ q.id ] !== "";

        this.app.view.render( this.root, questionTemplate, {
            house : this.survey.accommodation?.name || "",
            q : q,
            answer : this.answers[ q.id ],
            chips : this.chipsDraft,
            commentOpen : this.commentOpen,
            index : this.index,
            total : this.questions.length,
            canBack : this.index > 0,
            isLast : this.index === this.questions.length - 1,
            remainingLabel : _lcs( "Noch {count} Fragen", { count : remaining, n : remaining.toLocaleString( locale() ) } ),
            durationLabel : _lcs( "ca. {seconds} Sek.", { seconds : remaining * SECONDS_PER_QUESTION } ),
            smileys : SMILEYS.map( ( s ) => ( { ...s, label : _lcs( s.label ) } ) ),
            nps : Array.from( { length : 11 }, ( _, v ) => ( { value : v, tone : v <= 6 ? "low" : ( v <= 8 ? "mid" : "high" ) } ) )
        } );

        // Focus the prompt so screen readers announce each new question.
        this.root.querySelector( '[data-id="prompt"]' )?.focus( { preventScroll : true } );
    }

    _showDone( already = false )
    {
        this.app.view.render( this.root, doneTemplate, {
            house : this.survey?.accommodation?.name || "",
            already : already,
            thankYou : this.survey?.thankYouText || "",
            confetti : this.reducedMotion ? [] : Array.from( { length : 28 }, ( _, i ) => i ),
            homeLabel : this.app.user ? _lcs( "Zurück zur App" ) : _lcs( "Zur Gäste-App" )
        } );
    }

    _showError( status )
    {
        if ( status === 404 )
        {
            return this._message( { icon : "x", title : _lcs( "Dieser Link ist ungültig" ),
                text : _lcs( "Bitte prüfe den Link oder frag deine Gastgeber nach einem neuen." ), home : true } );
        }
        if ( status === 410 )
        {
            return this._message( { icon : "flag", title : _lcs( "Diese Umfrage ist nicht mehr verfügbar" ),
                text : _lcs( "Danke trotzdem, dass du dir Zeit nehmen wolltest!" ), home : true } );
        }
        return this._message( { icon : "mountain", title : _lcs( "Keine Verbindung" ),
            text : _lcs( "Die Umfrage konnte nicht geladen werden. Prüfe deine Internetverbindung." ), retry : true, home : true } );
    }

    _message( { icon, title, text = "", loading = false, retry = false, home = false } )
    {
        this.app.view.render( this.root, messageTemplate, { icon, title, text, loading, retry, home } );
    }

    /* -------------------------------- Actions -------------------------------- */

    _onClick( e )
    {
        const el = e.target.closest( "[data-action]" );
        if ( !el || el.disabled )
        {
            return;
        }

        const action = el.getAttribute( "data-action" );
        switch ( action )
        {
            case "answer":       this._answer( el, this._parseValue( el.getAttribute( "data-value" ) ) ); break;
            case "chip":         this._toggleChip( el ); break;
            case "chips-next":   this._submitChips(); break;
            case "comment-open": this._openComment(); break;
            case "comment-send": this._submitComment(); break;
            case "skip":         this._next(); break;
            case "back":         this._back(); break;
            case "close":
            case "home":         this.app.router.redirect( appHomeRoute( this.app ) ); break;
            case "retry":        this.survey ? this._finish() : this._load(); break;
        }
    }

    _onInput( e )
    {
        if ( e.target.getAttribute( "data-id" ) === "comment" )
        {
            const send = this.root.querySelector( '[data-action="comment-send"]' );
            if ( send )
            {
                send.disabled = e.target.value.trim() === "";
            }
        }
    }

    _parseValue( raw )
    {
        return /^-?\d+$/.test( raw ) ? parseInt( raw, 10 ) : raw;
    }

    /** A tap on a scale, thumb or tile: show it landing, store it, move on. */
    _answer( el, value )
    {
        if ( this.busy )
        {
            return;
        }
        this.busy = true;
        this._haptic();

        const q = this.questions[ this.index ];
        this.root.querySelectorAll( "[data-action='answer']" ).forEach( ( b ) =>
        {
            b.classList.remove( "is-picked" );
            b.setAttribute( "aria-pressed", "false" );
        } );
        el.classList.add( "is-picked" );
        el.setAttribute( "aria-pressed", "true" );

        // Stars light up to the tapped one, like every rating UI the guest knows.
        if ( q.type === "stars" )
        {
            this.root.querySelectorAll( ".survey-stars__btn" ).forEach( ( b ) =>
                b.classList.toggle( "is-on", +b.getAttribute( "data-value" ) <= value ) );
        }

        this._store( q.id, value );
        this.setTimeout( () =>
        {
            this.busy = false;
            this._next();
        }, this.reducedMotion ? 120 : ADVANCE_MS );
    }

    _toggleChip( el )
    {
        this._haptic();
        const key = el.getAttribute( "data-key" );
        const at = this.chipsDraft.indexOf( key );
        if ( at < 0 )
        {
            this.chipsDraft.push( key );
        }
        else
        {
            this.chipsDraft.splice( at, 1 );
        }
        el.classList.toggle( "is-on", at < 0 );
        el.setAttribute( "aria-pressed", String( at < 0 ) );

        const next = this.root.querySelector( '[data-action="chips-next"]' );
        if ( next )
        {
            next.textContent = this.chipsDraft.length > 0 ? _lcs( "Weiter" ) : _lcs( "Überspringen" );
        }
    }

    /** Nothing picked = skipped, not "liked nothing": an empty list would read as an answer. */
    _submitChips()
    {
        const q = this.questions[ this.index ];
        if ( this.chipsDraft.length > 0 )
        {
            this._store( q.id, this.chipsDraft.slice() );
        }
        this._next();
    }

    _openComment()
    {
        this.commentOpen = true;
        this.root.querySelector( '[data-id="comment-box"]' )?.removeAttribute( "hidden" );
        this.root.querySelector( '[data-id="comment-ask"]' )?.setAttribute( "hidden", "" );
        this.root.querySelector( '[data-id="comment"]' )?.focus();
    }

    _submitComment()
    {
        const q = this.questions[ this.index ];
        const text = ( this.root.querySelector( '[data-id="comment"]' )?.value || "" ).trim();
        if ( text )
        {
            this._store( q.id, text.slice( 0, 1000 ) );
        }
        this._next();
    }

    _next()
    {
        if ( this.index >= this.questions.length - 1 )
        {
            return this._finish();
        }
        this.index++;
        this._renderQuestion();
    }

    _back()
    {
        if ( this.index > 0 )
        {
            this.index--;
            this._renderQuestion();
        }
    }

    _haptic()
    {
        try
        {
            navigator.vibrate?.( 10 );
        }
        catch ( e )
        {
            // Not every browser allows it; a missing buzz is not an error.
        }
    }

    /* ------------------------- Saving & offline queue ------------------------ */

    /** Remember locally, then send. The queue entry is dropped once the server has it. */
    _store( questionId, value )
    {
        this.answers[ questionId ] = value;
        const queue = this._queue();
        queue[ questionId ] = value;
        this._saveQueue( queue );
        this._flush();
    }

    _queue()
    {
        const all = this.app.storage.load( QUEUE_KEY ) || {};
        return { ...( all[ this.token ] || {} ) };
    }

    _saveQueue( queue )
    {
        const all = { ...( this.app.storage.load( QUEUE_KEY ) || {} ) };
        if ( Object.keys( queue ).length > 0 )
        {
            all[ this.token ] = queue;
        }
        else
        {
            delete all[ this.token ];
        }
        this.app.storage.save( QUEUE_KEY, Object.keys( all ).length > 0 ? all : null );
    }

    /**
     * Send every queued answer. A network failure leaves the rest queued for
     * the next tap, the `online` event or the final submit. A 4xx is dropped:
     * retrying an answer the server rejects (or a survey already submitted)
     * can never succeed. Returns true when the queue is empty afterwards.
     */
    async _flush()
    {
        if ( this._flushing )
        {
            return this._flushing;
        }

        this._flushing = ( async () =>
        {
            for ( const [ questionId, value ] of Object.entries( this._queue() ) )
            {
                if ( !this.app )
                {
                    return false;
                }
                try
                {
                    await this.app.api.put(
                        "/survey/invite/" + encodeURIComponent( this.token ) + "/answer/" + questionId, { value } );
                }
                catch ( e )
                {
                    const status = +e?.status;
                    if ( !status || status >= 500 )
                    {
                        return false;
                    }
                    console.error( e );
                }

                if ( !this.app )
                {
                    return false;
                }

                // Only this entry: a newer tap on the same question may have
                // replaced it while the request was in flight.
                const queue = this._queue();
                if ( JSON.stringify( queue[ questionId ] ) === JSON.stringify( value ) )
                {
                    delete queue[ questionId ];
                    this._saveQueue( queue );
                }
            }
            return !!this.app && Object.keys( this._queue() ).length === 0;
        } )();

        try
        {
            return await this._flushing;
        }
        finally
        {
            this._flushing = null;
        }
    }

    /** Send what is left, submit, celebrate. Offline → a retry screen; nothing is lost. */
    async _finish()
    {
        this._message( { icon : "flag", title : _lcs( "Fast oben …" ), loading : true } );

        const flushed = await this._flush();
        if ( !this.app )
        {
            return; // Left the screen meanwhile.
        }
        if ( !flushed )
        {
            return this._message( { icon : "mountain", title : _lcs( "Keine Verbindung" ),
                text : _lcs( "Deine Antworten sind gespeichert und werden gesendet, sobald du wieder online bist." ), retry : true } );
        }

        try
        {
            await this.app.api.post( "/survey/invite/" + encodeURIComponent( this.token ) + "/complete", {} );
        }
        catch ( e )
        {
            console.error( e );
            const status = +e?.status;
            if ( status === 404 || status === 410 )
            {
                return this._showError( status );
            }
            return this._message( { icon : "mountain", title : _lcs( "Keine Verbindung" ),
                text : _lcs( "Deine Antworten sind gespeichert und werden gesendet, sobald du wieder online bist." ), retry : true } );
        }

        if ( this.app )
        {
            this.app.tracker.track( "survey_done" );
            this._haptic();
            this._showDone( false );
        }
    }
}
